import { FC, PropsWithChildren, MouseEvent, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '@/context/NavigationContext';

const CARD_TOP_OFFSET = 56;
const CARD_MAX_WIDTH = 1800;
const CARD_VIEWPORT_WIDTH = 93; // percent of viewport width for top card
const BEHIND_STEP_OFFSET = 18;
const BEHIND_MAX_DEPTH = 2;

const useViewportWidth = () => {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
};

interface OverlayCardProps {
  title?: string;
  variant?: 'top' | 'behind';
  // 0 for top, 1 for just behind, 2 for further back
  behindIndex?: number;
  backgroundLocation?: LocationLike;
}

type LocationLike = {
  pathname: string;
  search?: string;
  hash?: string;
  state?: unknown;
};

const OverlayCard: FC<PropsWithChildren<OverlayCardProps>> = ({
  children,
  title,
  variant = 'top',
  behindIndex = 0,
  backgroundLocation,
}) => {
  const navigate = useNavigate();
  const { lastBaseLocation } = useNavigation();
  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth < 768;

  const depth = Math.min(behindIndex, BEHIND_MAX_DEPTH);
  const topOffset = isMobile ? 20 : CARD_TOP_OFFSET;
  const behindStep = isMobile ? 14 : BEHIND_STEP_OFFSET;
  const cardWidthVw = isMobile ? 98 : CARD_VIEWPORT_WIDTH;
  const topWidthValue = isMobile
    ? `min(${cardWidthVw}vw, ${CARD_MAX_WIDTH}px)`
    : `${CARD_VIEWPORT_WIDTH}vw`;
  const topMaxOffset = Math.max(topOffset - (isMobile ? 6 : 16), 0);
  const topStyle: CSSProperties = {
    top: topOffset,
    left: '50%',
    width: topWidthValue,
    maxWidth: `${CARD_MAX_WIDTH}px`,
    maxHeight: `calc(100vh - ${topMaxOffset}px)`,
    zIndex: 220,
  };

  const behindWidthVw = isMobile
    ? Math.max(cardWidthVw - 4 - depth * 3, 84)
    : Math.max(CARD_VIEWPORT_WIDTH - 6 - depth * 4, 60);
  const behindWidthValue = isMobile
    ? `min(${behindWidthVw}vw, ${Math.max(CARD_MAX_WIDTH - depth * 24, 720)}px)`
    : `${behindWidthVw}vw`;
  const behindTop = topOffset + (isMobile ? 18 : 24) + depth * behindStep;
  const behindMaxOffset = Math.max(behindTop - (isMobile ? 6 : 24), 0);
  const behindStyle: CSSProperties = {
    top: behindTop,
    left: '50%',
    width: behindWidthValue,
    maxWidth: `${CARD_MAX_WIDTH - depth * 24}px`,
    maxHeight: `calc(100vh - ${behindMaxOffset}px)`,
    zIndex: 200 - depth * 6,
  };

  useEffect(() => {
    if (variant !== 'top') return;

    const previousBody = document.body.style.overflow;
    const previousHtml = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBody;
      document.documentElement.style.overflow = previousHtml;
    };
  }, [variant]);

  return (
    <>
      {/* No fullscreen backdrop so landing/nav remain visible & clickable */}

      {/* Soft halo behind the card to sell the stack animation */}
      {variant === 'top' && (
        <motion.div
          className="fixed bottom-0 z-[180] pointer-events-none rounded-t-[44px] bg-zinc-900/12 shadow-[0_50px_120px_-70px_rgba(15,23,42,0.55)] dark:bg-zinc-900/35"
          style={{
            top: topOffset - (isMobile ? 18 : 24),
            left: '50%',
            width: `${Math.min(isMobile ? cardWidthVw + 2 : CARD_VIEWPORT_WIDTH + 8, isMobile ? 100 : 96)}vw`,
            maxWidth: `${CARD_MAX_WIDTH + 80}px`,
            filter: 'blur(18px)',
          }}
          initial={{ opacity: 0, y: 40, x: '-50%' }}
          animate={{ opacity: 1, y: 0, x: '-50%' }}
          exit={{ opacity: 0, y: 30, x: '-50%' }}
          transition={{ duration: 0.55, ease: [0.2, 0.8, 0.2, 1] }}
          aria-hidden
        />
      )}

      {/* Main stacked card */}
      <motion.div
        className={`fixed overflow-hidden transform-gpu ${
          variant === 'behind'
            ? 'bottom-4 sm:bottom-6 pointer-events-none rounded-t-[32px] sm:rounded-t-[36px] bg-white/70 ring-1 ring-black/5 shadow-[0_45px_120px_-80px_rgba(15,23,42,0.55)] backdrop-blur-md dark:bg-zinc-900/55 dark:ring-white/10'
            : 'bottom-0 pointer-events-auto rounded-t-[32px] sm:rounded-t-[46px] lg:rounded-t-[52px] bg-white/95 ring-1 ring-zinc-200/70 shadow-[0_70px_140px_-80px_rgba(15,23,42,0.6)] backdrop-blur-xl dark:bg-zinc-900/95 dark:ring-zinc-700/60'
        }`}
        style={variant === 'behind' ? behindStyle : topStyle}
        initial={
          variant === 'behind'
            ? {
                opacity: 0,
                y: 72,
                x: '-50%',
                scale: 0.95 - depth * 0.02,
              }
            : { opacity: 0, y: '100%', x: '-50%' }
        }
        animate={
          variant === 'behind'
            ? {
                opacity: 0.65,
                y: 0,
                x: '-50%',
                scale: 0.97 - depth * 0.015,
              }
            : { opacity: 1, y: 0, x: '-50%' }
        }
        exit={
          variant === 'behind'
            ? {
                opacity: 0,
                y: 72,
                x: '-50%',
                scale: 0.95 - depth * 0.02,
              }
            : { opacity: 0, y: '100%', x: '-50%' }
        }
        transition={
          variant === 'behind'
            ? { duration: 0.55, ease: [0.2, 0.8, 0.2, 1] }
            : { type: 'spring', stiffness: 260, damping: 32, mass: 1.15 }
        }
        onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Scoped CSS to hide site header/footer inside overlays */}
        <style>{`.overlay-content [data-role="site-header"], .overlay-content footer { display: none !important; }`}</style>

        {/* Large round close button inside card */}
        {variant === 'top' && (
          <button
            aria-label="Close overlay"
            onClick={() => {
              const fallback = backgroundLocation || lastBaseLocation || null;
              const target = fallback
                ? `${fallback.pathname}${fallback.search || ''}${fallback.hash || ''}`
                : '/';

              if (fallback && fallback.state !== undefined) {
                navigate(target || '/', { state: fallback.state });
              } else {
                navigate(target || '/');
              }
            }}
            className="absolute top-7 right-8 z-[221] flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200/70 bg-white/85 text-zinc-600 shadow-sm backdrop-blur-sm transition-colors hover:bg-white hover:text-zinc-900 dark:border-zinc-600/40 dark:bg-zinc-800/80 dark:text-zinc-200 dark:hover:bg-zinc-800"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6l12 12" />
              <path d="M18 6l-12 12" />
            </svg>
          </button>
        )}

        {/* Content area scrolls independently */}
        <div className="overlay-content absolute inset-0 overflow-auto">
          {children}
        </div>
      </motion.div>
    </>
  );
};

export default OverlayCard;
